/* ==========================================================================
   AJ FITS — Stock limits
   One source of truth for "how many more of this variant may be added".

   Every buying surface (PDP stepper, quick add, quick view, frequently
   bought together, cart + drawer steppers) asks this module before it lets a
   quantity go up, so a variant with 3 units in stock can never be pushed past
   3 — counting whatever is already sitting in the cart.

   Shopify still rejects an oversell server side; this layer exists so the
   shopper is told before they hit that wall, in the theme's own wording.
   ========================================================================== */
(function () {
  'use strict';

  var AJ = (window.AJ = window.AJ || {});

  /* variantId -> units already in the cart. Empty until the first sync. */
  var counts = {};

  function strings() {
    return window.ajStockStrings || {};
  }

  function fill(template, max, inCart) {
    return String(template)
      .replace(/\[max\]/g, max)
      .replace(/\[incart\]/g, inCart);
  }

  function warnIcon() {
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="13"/>' +
      '<line x1="12" y1="16.5" x2="12" y2="16.6"/></svg>'
    );
  }

  var stock = {
    /* ------------------------------------------------------------------
       Cart state
       ------------------------------------------------------------------ */

    /* Units of `variantId` already in the cart, across every line. */
    inCart: function (variantId) {
      var n = counts[String(variantId)];
      return typeof n === 'number' ? n : 0;
    },

    /* Rebuild the cache from a /cart.js payload. */
    sync: function (cart) {
      if (!cart || !cart.items) return cart;

      var next = {};
      cart.items.forEach(function (item) {
        var key = String(item.variant_id);
        next[key] = (next[key] || 0) + item.quantity;
      });
      counts = next;

      document.dispatchEvent(new CustomEvent('aj:stock:change'));
      return cart;
    },

    refresh: function () {
      if (!AJ.cart || !AJ.cart.get) return Promise.resolve(null);
      return AJ.cart
        .get()
        .then(stock.sync)
        .catch(function () { return null; });
    },

    /* ------------------------------------------------------------------
       Limits
       ------------------------------------------------------------------ */

    /* '' / null / undefined all mean "not stock capped" — sell freely. */
    parse: function (value) {
      if (value === undefined || value === null || value === '') return Infinity;
      var n = parseInt(value, 10);
      return isNaN(n) || n < 0 ? Infinity : n;
    },

    capped: function (value) {
      return stock.parse(value) !== Infinity;
    },

    /* How many more units of this variant may be added right now. */
    remaining: function (variantId, max) {
      var cap = stock.parse(max);
      if (cap === Infinity) return Infinity;
      return Math.max(0, cap - stock.inCart(variantId));
    },

    /* ------------------------------------------------------------------
       Messaging
       ------------------------------------------------------------------ */

    /*
      Why the quantity stopped. With an empty cart the ceiling is simply the
      stock; once the cart holds some of it, the shortfall is what needs
      explaining, so the wording names both numbers.
    */
    message: function (max, inCart) {
      if (inCart > 0) {
        return fill(
          strings().maxQtyInCart || 'Only [max] in stock — [incart] already in your cart.',
          max,
          inCart
        );
      }
      return fill(
        strings().maxQty || "Maximum quantity is [max] — you can't add more than that.",
        max,
        inCart || 0
      );
    },

    /*
      Say it out loud. `note` is an optional inline element filled in as well
      as the toast, so the reason stays on screen next to the control that
      refused rather than fading with the toast.
    */
    warn: function (max, note, inCart) {
      var msg = stock.message(max, inCart || 0);

      if (note) {
        note.textContent = msg;
        note.hidden = false;
      }
      if (AJ.toast) AJ.toast(msg, { type: 'error', icon: warnIcon() });

      return msg;
    },

    clearNote: function (note) {
      if (!note) return;
      note.textContent = '';
      note.hidden = true;
    },

    /*
      Shopify's own 422 reads "You can only add 3 of Product to the cart."
      Re-say it in the theme's wording when we can, otherwise pass it through.
    */
    friendlyError: function (err) {
      var raw = (err && err.message) || '';
      var match = raw.match(/you can only add (\d+)/i);
      if (match) return stock.message(match[1]);
      return raw || 'Could not add to cart';
    },

    /*
      Guard an add-to-cart before the request goes out. Returns true when the
      line is allowed; warns and returns false when it is not.
    */
    allow: function (variantId, max, wanted, note) {
      var left = stock.remaining(variantId, max);
      if (left === Infinity || (wanted || 1) <= left) return true;

      stock.warn(stock.parse(max), note, stock.inCart(variantId));
      return false;
    }
  };

  AJ.stock = stock;

  /* ---------------------------------------------------------------------
     Keep the cache warm
     --------------------------------------------------------------------- */
  document.addEventListener('aj:cart:change', function (e) {
    if (e.detail && e.detail.cart) stock.sync(e.detail.cart);
  });

  /* Dawn's own cart updates (cart page + drawer) do not go through AJ.cart. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () { stock.refresh(); });
  }

  function boot() { stock.refresh(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* ---------------------------------------------------------------------
     Cart + drawer steppers
     Dawn's <quantity-input> already refuses to step past `max`; it just does
     it silently. Delegate a click listener so a stock-capped line explains
     itself instead.
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var btn = e.target.closest('.quantity__button[name="plus"]');
    if (!btn) return;

    var host = btn.closest('quantity-input');
    var input = host && host.querySelector('.quantity__input');
    if (!input || input.dataset.stockCapped !== 'true') return;

    var max = stock.parse(input.max);
    if (max === Infinity) return;

    // The line itself is the cart holding, so the plain "that's the lot"
    // wording is the right one here.
    if (parseInt(input.value, 10) >= max) stock.warn(max, null, 0);
  });

  /*
    Typing straight into the field skips the buttons entirely. Clamp it here,
    before Dawn's debounced change handler reads the value and posts it.
  */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.classList || !input.classList.contains('quantity__input')) return;
    if (input.dataset.stockCapped !== 'true') return;

    var max = stock.parse(input.max);
    if (max === Infinity) return;

    if ((parseInt(input.value, 10) || 0) > max) {
      input.value = max;
      stock.warn(max, null, 0);
    }
  });
})();
